if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.productsCachedMap = new Map();
        this.focusElement = this.input;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');

        // drop stale product suggestions with the rest of the results
        this.productsAbortController?.abort();
        const container = this.querySelector('[data-predictive-products]');
        if (container) container.innerHTML = '';
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl() {
        // window.location.origin, not theme.routes.shop_url: shop_url comes from
        // {{ request.origin }} (snippets/js-variables.liquid), which the `shopify theme dev`
        // proxy resolves to the myshopify domain — so from 127.0.0.1:9292 this fetch went
        // cross-origin, failed CORS, and left the drawer spinning forever (the catch below
        // never clears `loading`). Identical to shop_url in production, same-origin always.
        const url = new URL(`${window.location.origin}${theme.routes.predictive_search_url}`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      // Product suggestions come from regular search, not /search/suggest: outdoor units
      // (hvac.unit_type outdoor / outdoor_multi) are only reachable through the
      // konfigurátor and dominate the search index, and the predictive search API accepts
      // no filters — so the dropdown's product list was empty once they were dropped.
      // Regular search takes the indoor filter, and sections/predictive-products.liquid
      // also skips outdoor units itself in case the filter isn't registered yet.
      buildProductsUrl() {
        // same-origin, for the reason explained in buildUrl()
        const url = new URL(`${window.location.origin}${theme.routes.search_url}`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        // include-only filter, OR'd: indoor units + accessories. Must stay in sync with the
        // hidden inputs in snippets/predictive-search.liquid.
        url.searchParams.append('filter.p.m.hvac.unit_type', 'indoor');
        url.searchParams.append('filter.p.m.hvac.unit_type', 'accessory');
        url.searchParams.set('section_id', 'predictive-products');
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);
        this.renderProductSuggestions();

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);
            this.renderProductSuggestions();

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(id).innerHTML;
      }

      // renderSearchResults() has just replaced the results markup, so the container is a
      // fresh element — always re-query it rather than holding a reference.
      renderProductSuggestions() {
        if (this.querySelector('[data-predictive-products]') === null) return;

        const url = this.buildProductsUrl().toString();

        if (this.productsCachedMap.has(url)) {
          this.injectProductSuggestions(this.productsCachedMap.get(url));
          return;
        }

        this.productsAbortController?.abort();
        this.productsAbortController = new AbortController();

        fetch(url, { signal: this.productsAbortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.productsCachedMap.set(url, responseText);
            this.injectProductSuggestions(responseText);
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Product suggestions fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      injectProductSuggestions(responseText) {
        const container = this.querySelector('[data-predictive-products]');
        if (container === null) return;

        const rendered = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('[data-predictive-products]');

        container.innerHTML = rendered ? rendered.innerHTML : '';

        // The "no results" state is rendered from the /search/suggest resources alone
        // (products aren't known yet at that point), so drop it once products do arrive.
        if (container.innerHTML.trim() !== '') {
          this.querySelector('[data-search-empty]')?.remove();
        }
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
